import {useFormDispatch, useForm} from "../reducers";
import parse from "html-react-parser";
import {DataStore} from "@aws-amplify/datastore";
import {Submissions} from "../models";
import {DataGrid} from "@mui/x-data-grid";
import {styled} from '@mui/material/styles';
import {Box} from "@mui/material";
import {I18n as i18n} from "aws-amplify/utils";
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import {useEffect, useState} from "react";

export default function SubmissionList(props) {
    const dispatch = useFormDispatch();
    const context = useForm();
    const {form, user, pageNumber} = context;
    const [submissions, setSubmissions] = useState(context.submissions);

    const controlAttributes = JSON.parse(props.control.attributes);
    const headerNames = controlAttributes.fieldLabels || [];
    const defaultData = controlAttributes.defaultData;

    const updateSubmissions = async () => {
        const updatedSubmissions = await DataStore.query(Submissions);
        setSubmissions(updatedSubmissions);
    };

    useEffect(() => {
        console.log('list initialised');
        updateSubmissions();
    }, []);

    useEffect(() => {
        const subscription = DataStore.observe(Submissions).subscribe(msg => {
            if (msg.opType === 'UPDATE') {
                console.log('DataStore update:', msg.element);
                updateSubmissions();
            }
        });
        return () => subscription.unsubscribe();
    }, []);

    // rows
    let rows = submissions.map((submission) => {
        let rowData = { id: submission.id };
        controlAttributes.fields.forEach((fieldPath) => {
            const fieldParts = fieldPath.split('.');
            let currentValue = submission;
            for (let part of fieldParts) {
                currentValue = (currentValue && currentValue[part]) ? currentValue[part] : null;
                if (currentValue === null) break;
            }
            if (fieldPath === 'meta.complete') {
                currentValue = currentValue ? '✓' : '✕';
            } else if (typeof currentValue === 'boolean') {
                currentValue = currentValue ? '✓' : '✕';
            }
            if (fieldPath === 'meta.percentComplete') {
                currentValue = { isProgress: true, value: currentValue };
            }
            rowData[fieldPath.replace(/\./g, '_')] = currentValue;
        });
        return rowData;
    });

    const BorderLinearProgress = styled(LinearProgress)(({theme, value}) => ({
        height: 10,
        borderRadius: 5,
        width: 120,
        [`&.${linearProgressClasses.colorPrimary}`]: {
            backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
        },
        [`& .${linearProgressClasses.bar}`]: {
            borderRadius: 5,
            backgroundColor: value < 100 ? '#ff9800' : '#4CAF50',
        },
    }));

    // column sizing from attributes
    const columnWidths = controlAttributes.columnWidths || [];
    const totalFields = controlAttributes.fields?.length || 0;
    const perColMin = Math.max(80, Math.floor(600 / Math.max(1, totalFields)));

    // collect % columns -> integer flex weights (ONLY % columns get flex)
    const percentFlex = new Map(); // index -> flex int
    columnWidths.forEach((w, i) => {
        if (typeof w === "string" && w.trim().endsWith("%")) {
            const num = parseFloat(w);
            if (!isNaN(num)) percentFlex.set(i, Math.max(1, Math.round(num))); // e.g. 65 -> 65
        }
    });

    // ---- SORT MODEL (driven by attributes.sortColumn) -------------------
    const sortLabel = controlAttributes.sortColumn;
    const sortIndex = Array.isArray(headerNames) ? headerNames.findIndex(h => h === sortLabel) : -1;
    const sortFieldPath = sortIndex >= 0 ? controlAttributes.fields?.[sortIndex] : null;
    const sortFieldKey = sortFieldPath ? sortFieldPath.replace(/\./g, '_') : null;

    const [sortModel, setSortModel] = useState(
        sortFieldKey ? [{ field: sortFieldKey, sort: 'asc' }] : []
    );
    useEffect(() => {
        const idx = Array.isArray(headerNames) ? headerNames.findIndex(h => h === controlAttributes.sortColumn) : -1;
        const fp = idx >= 0 ? controlAttributes.fields?.[idx] : null;
        const fk = fp ? fp.replace(/\./g, '_') : null;
        setSortModel(fk ? [{ field: fk, sort: 'asc' }] : []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlAttributes.sortColumn]);
    // --------------------------------------------------------------------

    // columns
    let cols = controlAttributes.fields.map((fieldPath, i) => {
        const dataGridFieldKey = fieldPath.replace(/\./g, '_');
        const isPercentComplete = fieldPath === 'meta.percentComplete';
        const isCompleteCheck = fieldPath === 'meta.complete';
        const isInitialSortCol = i === sortIndex;

        // apply sizing rules:
        // replace your sizing ternary with this exact order
        const widthSetting = columnWidths[i];
        const s = typeof widthSetting === "string" ? widthSetting.trim() : null;

        const sizing =
            typeof widthSetting === "number"
                ? { width: widthSetting, minWidth: widthSetting }                            // fixed px (number)
                : (s && s.endsWith("%") && percentFlex.has(i))
                    ? { flex: percentFlex.get(i), minWidth: perColMin }                      // % -> flex (ONLY % columns flex)
                    : (s && /^\d+(\s*px)?$/i.test(s))
                        ? { width: parseInt(s, 10), minWidth: parseInt(s, 10) }              // "140" or "140px" -> px
                        : { minWidth: perColMin };                                           // default: no flex


        return {
            field: dataGridFieldKey,
            headerName: headerNames[i],
            headerClassName: 'boldHeader',
            ...sizing,
            ...(isPercentComplete ? {
                // numeric sorting though render uses object
                valueGetter: (params) => {
                    const v = params.row?.[dataGridFieldKey];
                    return (v && typeof v === 'object' && v.isProgress)
                        ? (typeof v.value === 'number' ? v.value : Number(v.value) || 0)
                        : (typeof v === 'number' ? v : Number(v) || 0);
                }
            } : {}),
            ...(isCompleteCheck ? {
                sortComparator: (v1, v2) => {
                    const a = v1 === '✓' ? 1 : 0;
                    const b = v2 === '✓' ? 1 : 0;
                    return a - b;
                }
            } : {}),
            ...(isInitialSortCol ? {
                // numeric sorting for configured initial sort column; no type change (keeps left align)
                valueGetter: (params) => {
                    const raw = params.row?.[dataGridFieldKey];
                    const n = (typeof raw === 'number')
                        ? raw
                        : (raw == null ? null : Number(String(raw).replace(/[^0-9.+-]/g, '')));
                    return Number.isFinite(n) ? n : -Infinity;
                },
                sortComparator: (a, b) => (a ?? -Infinity) - (b ?? -Infinity)
            } : {}),
            renderCell: (params) => {
                if (params.value && params.value.isProgress) {
                    const progressValue = params.value.value;
                    return (<BorderLinearProgress variant="determinate" value={progressValue} />);
                }
                return <span>{params.value}</span>;
            }
        };
    });

    async function newSubmission() {
        const team = [];
        const newSubmission = new Submissions({
            meta: {
                page: 0,
                submissionLanguage: 'en',
                owner: JSON.stringify({
                    familyName: user?.familyName,
                    givenName: user?.givenName,
                    email: user?.email,
                    userId: user?.id,
                }),
                creator: JSON.stringify({
                    familyName: user?.familyName,
                    givenName: user?.givenName,
                    email: user?.email,
                    userId: user?.id,
                }),
                team,
                percentComplete: 0,
                complete: false,
                trackingNumber: null
            },
            data: defaultData,
            files: [],
            hidden: false,
            formToken: form.token,
            token: "red herring",
            checked: false,
            projectToken: form.projectToken,
            userId: user?.id,
        });

        try {
            const savedSubmission = await DataStore.save(newSubmission);
            setSubmissions([...submissions, savedSubmission]);
            dispatch({ type: "LOAD_SUBMISSIONS", value: [...submissions, savedSubmission] });
            load(savedSubmission.id);
        } catch (e) {
            console.log(e);
        }
    }

    function load(id) {
        if (!id) {
            console.warn('Attempted to load submission with invalid id:', id);
            return;
        }
        dispatch({ type: "SET_SUBMISSION_ID", value: id });
        dispatch({ type: "SET_PAGE_NUMBER", value: pageNumber + 1 });
    }

    const columnHeaderProp = headerNames.length ? {} : { columnHeaderHeight: 0 };

    return (
        <>
            <p>{parse(i18n.get(props.control.content))}</p>
            {rows && rows.length > 0 && (
                <Box mb={2} sx={{ width: '100%', overflowX: 'auto' }}>
                    <DataGrid
                        rows={rows}
                        columns={cols}
                        {...columnHeaderProp}
                        sx={{
                            width: '100%',
                            '& .MuiDataGrid-row:hover': {
                                backgroundColor: 'transparent',
                                color: 'blue',
                                fontWeight: 'bold',
                            },
                        }}
                        onRowClick={(rowParams) => load(rowParams.id)}
                        hideFooter
                        sortingOrder={['asc','desc']}
                        sortModel={sortModel}
                        onSortModelChange={(m) => setSortModel(m)}
                    />
                </Box>
            )}
            {rows.length < (controlAttributes.maxSubmissions ?? 1) && controlAttributes.addNewButton && (
                <div key={props.currentKey + "1"}>
                    <button onClick={newSubmission}>
                        {i18n.get(controlAttributes?.addNewLabel)}
                    </button>
                </div>
            )}
        </>
    );
}
